import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import Dashboard from '../pages/Dashboard';
import TaskBoard from '../pages/TaskBoard';
import Upload from '../pages/Upload';
import Settings from '../pages/Settings';

const Layout: React.FC = () => {
  const { loading, vellrixUser } = useAuth();

  const [currentPage, setCurrentPage] = useState('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('vellrix_darkMode');
    if (savedDarkMode) {
      const darkMode = JSON.parse(savedDarkMode);
      setIsDarkMode(darkMode);
      if (darkMode) {
        document.body.classList.add('dark');
      }
    }
  }, []);

  // Handle hash-based navigation (e.g., /app#operations)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === 'operations') {
        setCurrentPage('settings');
      }
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleToggleDarkMode = useCallback(() => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    document.body.classList.toggle('dark', newDarkMode);
    localStorage.setItem('vellrix_darkMode', JSON.stringify(newDarkMode));
  }, [isDarkMode]);

  const handlePageChange = (page: string) => {
    setCurrentPage(page);
    // Clear hash when changing pages
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.ctrlKey) {
        switch (e.key) {
          case 'd':
          case 'D':
            e.preventDefault();
            handleToggleDarkMode();
            break;
          case '1':
            e.preventDefault();
            setCurrentPage('dashboard');
            break;
          case '2':
            e.preventDefault();
            setCurrentPage('tasks');
            break;
          case '3':
            e.preventDefault();
            setCurrentPage('upload');
            break;
          case '4':
            e.preventDefault();
            setCurrentPage('settings');
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [handleToggleDarkMode]);

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'tasks':
        return <TaskBoard />;
      case 'upload':
        return <Upload />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <div className="flex h-screen">
        <Sidebar
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onToggleDarkMode={handleToggleDarkMode}
          isDarkMode={isDarkMode}
        />
        <main className="flex-1 overflow-auto">
          {loading || !vellrixUser ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-lg" style={{ color: 'var(--text-primary)' }}>Loading...</p>
              </div>
            </div>
          ) : (
            renderCurrentPage()
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
